/**
 * Minimal vector / scalar math.
 *
 * This module (and everything else in `shared/`) must stay dependency-free —
 * it is imported by the Cloudflare Worker, which has no DOM and must never
 * pull in Three.js. Anything here also runs inside the client's prediction
 * replay loop, so it avoids allocation on hot paths by operating on flat
 * numbers rather than object wrappers.
 */

export const TAU = Math.PI * 2;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Wrap an angle into [-PI, PI).
 *
 * Lessons-learned #4: naive lerping of yaw spins the long way around whenever
 * the value crosses the +/-PI seam. Every angle that gets interpolated must be
 * pushed through here first.
 */
export function wrapAngle(a: number): number {
  let x = (a + Math.PI) % TAU;
  if (x < 0) x += TAU;
  return x - Math.PI;
}

/** Shortest-path angular interpolation. Never takes the long way round. */
export function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + wrapAngle(b - a) * t);
}

/** Signed shortest angular distance from `a` to `b`, in [-PI, PI). */
export function angleDelta(a: number, b: number): number {
  return wrapAngle(b - a);
}

/** Squared horizontal (XZ) distance — avoids a sqrt in broadphase/LOD checks. */
export function distSq2D(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return dx * dx + dz * dz;
}

export function distSq3D(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): number {
  const dx = ax - bx;
  const dy = ay - by;
  const dz = az - bz;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Exponential moving average.
 *
 * Lessons-learned: clock offset estimates must be smoothed, never snapped to
 * the latest sample — one delayed ping should not jerk the shared timeline.
 */
export function ema(previous: number, sample: number, alpha: number): number {
  return previous + (sample - previous) * alpha;
}
